/**
 * WYSIWYG Agent Dashboard Page
 *
 * Aggregate view of WYSIWYG-routed agent activity: stats, charts, recent executions.
 */
import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  DocumentTextIcon,
  ClockIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { wysiwygStore } from '@/stores/WYSIWYGStore'

const COLORS = ['#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']

const WYSIWYGDashboardPage = observer(() => {
  const navigate = useNavigate()
  const { dashboard, executions, isLoading } = wysiwygStore

  useEffect(() => {
    wysiwygStore.fetchDashboard()
    wysiwygStore.fetchExecutions({ limit: 10 })
  }, [])

  const actionChartData = dashboard?.by_action_type
    ? Object.entries(dashboard.by_action_type).map(([action, count], idx) => ({
        action,
        runs: count,
        fill: COLORS[idx % COLORS.length],
      }))
    : []

  const specialistChartData = dashboard?.by_specialist
    ? Object.entries(dashboard.by_specialist).map(([specialist, count], idx) => ({
        specialist: specialist.replace('wysiwyg_', ''),
        runs: count,
        fill: COLORS[idx % COLORS.length],
      }))
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <DocumentTextIcon className="w-7 h-7 text-violet-600" />
            Editor AI
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor AI tasks routed from docs.ziro.pub to Ziro Intelligence
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            wysiwygStore.fetchDashboard()
            wysiwygStore.fetchExecutions({ limit: 10 })
          }}
        >
          <ArrowPathIcon className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Executions</p>
          <p className="text-2xl font-bold text-foreground mt-1">
            {dashboard?.total_executions ?? 0}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <ClockIcon className="w-3.5 h-3.5" /> Avg Latency
          </div>
          <p className="text-2xl font-bold text-foreground mt-1">
            {dashboard?.avg_latency_ms ? `${(dashboard.avg_latency_ms / 1000).toFixed(1)}s` : '--'}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <SparklesIcon className="w-3.5 h-3.5" /> Avg Quality
          </div>
          <p className="text-2xl font-bold text-foreground mt-1">
            {dashboard?.avg_quality ? `${dashboard.avg_quality.toFixed(1)}/10` : '--'}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <CurrencyDollarIcon className="w-3.5 h-3.5" /> Total Cost
          </div>
          <p className="text-2xl font-bold text-foreground mt-1">
            ${dashboard?.total_cost_usd?.toFixed(2) ?? '0.00'}
          </p>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Executions by Action</h3>
          {actionChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={actionChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="action" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="runs" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Executions by Specialist</h3>
          {specialistChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={specialistChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="specialist" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="runs" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
          )}
        </Card>
      </div>

      {/* Recent Executions */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Executions</h3>
        {executions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="pb-2 font-medium">Time</th>
                  <th className="pb-2 font-medium">Prompt</th>
                  <th className="pb-2 font-medium">Actions</th>
                  <th className="pb-2 font-medium">Quality</th>
                  <th className="pb-2 font-medium">Cost</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exec) => (
                  <tr key={exec.id} className="border-b border-border/50">
                    <td className="py-2 text-muted-foreground">{new Date(exec.created_at).toLocaleString()}</td>
                    <td className="py-2 max-w-[200px] truncate">{exec.prompt}</td>
                    <td className="py-2">{exec.remote_tasks} task(s)</td>
                    <td className="py-2">{exec.quality_score?.toFixed(1) ?? '--'}/10</td>
                    <td className="py-2">${exec.cost_usd.toFixed(3)}</td>
                    <td className="py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/wysiwyg/executions/${exec.id}`)}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            {isLoading ? 'Loading...' : 'No WYSIWYG executions yet. Use the AI chat in docs.ziro.pub to trigger one.'}
          </p>
        )}
      </Card>
    </div>
  )
})

export default WYSIWYGDashboardPage
